import React, { CSSProperties, FunctionComponent } from 'react';
import { StyledText } from 'rs-emd-ui-atoms';
import { ITextDataWidget } from '../../../../models/campaign-pages/widgets/text/text-data.model';
import { ITextEditor } from '../../../../models/campaign-pages/common.model';
import { TextTagTypeConvertor } from '../common';
import styles from './text-widget-component.module.scss';

export type TextWidgetProps = {
  textWidget: ITextDataWidget;
  isContainerFullHeight?: boolean;
};

export const TextWidget: FunctionComponent<TextWidgetProps> = (props: TextWidgetProps) => {
  function getEmbeddedUrl(text: ITextEditor, placeholder: string) {
    //check if embedded url within placeholder
    let embeddedUrl = text.embeddedUrls?.filter((embed) => embed.placeholder === placeholder)[0];

    if (embeddedUrl) {
      return (
        <a href={embeddedUrl.URL ?? ''} target={embeddedUrl.target ?? ''}>
          {embeddedUrl.label}
        </a>
      );
    }
    //fallback just in case user inputs text within '<|' '|>' which is not a link
    else {
      return placeholder;
    }
  }

  return (
    <div
      className={`${styles['text-container']} ${styles['desktop-vert-align-' + props.textWidget.textAlignment?.desktop]} ${
        styles['mobile-vert-align-' + props.textWidget.textAlignment?.mobile]
      }`}
      style={{ '--container-height': props.isContainerFullHeight ? '100%' : 'auto' } as CSSProperties}>
      {props.textWidget.textData?.map((txt) => {
        return (
          <StyledText
            tagType={TextTagTypeConvertor[txt.textSize ?? 'paragraph']}
            color={txt.colour ?? undefined}
            size={txt.textSize ?? 'paragraph'}
            weight={{ isBold: txt.isBold ?? false }}
            decoration={{
              isStrikethrough: txt.isStrikethrough ?? false,
              isUnderline: txt.isUnderline ?? false,
            }}
            styling={{ isItalic: txt.isItalic ?? false }}
            padding={txt.padding ?? undefined}
            alignment={txt.alignment ?? undefined}>
            {/** outputting the user's text whilst generating anchor tags as required */}
            {txt.text &&
              //split text by words(placeholders) which start with '<|' and end with '|>'
              txt.text.split(/(<\|.*?\|>)/g).map((txtContent) => {
                //if text start with '<|' and end with '|>' may be an embedded list or embedded url or simply text
                if (isPlaceholder(txtContent)) {
                  //check if embedded list
                  let embeddedList = txt.embeddedLists?.filter((embed) => embed.placeholder === txtContent)[0];

                  if (embeddedList) {
                    let ListTag = embeddedList.listType as keyof JSX.IntrinsicElements; //determine wheteher ordered list or not

                    return (
                      <div
                        className={`${styles['list-wrapper']} ${styles[`list-align-${txt.alignment?.desktop}`]} ${
                          styles[`mobile-list-align-${txt.alignment?.mobile}`]
                        }`}>
                        <ListTag className={styles.list}>
                          {embeddedList?.listItems?.map((l) => {
                            return (
                              <li key={`styled-list-item-${l.position}`}>
                                {l.value?.split(/(<\|.*?\|>)/g).map((listVal) => {
                                  //check if any placeholders in list value and if so whether they are embedded urls
                                  if (isPlaceholder(listVal)) {
                                    return getEmbeddedUrl(txt, listVal);
                                  }
                                  return listVal;
                                })}
                              </li>
                            );
                          })}
                        </ListTag>
                      </div>
                    );
                  } else {
                    //check if embedded url
                    return getEmbeddedUrl(txt, txtContent);
                  }
                } else {
                  return txtContent;
                }
              })}
          </StyledText>
        );
      })}
    </div>
  );

  function isPlaceholder(placeholder: string): boolean {
    return placeholder.startsWith('<|') && placeholder.endsWith('|>');
  }
};

TextWidget.defaultProps = {
  isContainerFullHeight: true,
};

export default TextWidget;
